/**
 * Company brand (ADR-023) — one brand for every workspace the Company links:
 * the signing pages its recipients see and the sign-off on its emails.
 *
 * Rendered only when the Company holds `company_branding`, which only Fias
 * grants (the Company nav hides the section too). Hidden rather than disabled
 * otherwise: a disabled button advertises a capability and invites a support
 * ticket that ends in "you cannot have this". The server
 * enforces the entitlement on every write; this is the affordance. A failed
 * load is shown, never hidden, so an outage does not read as "no branding".
 *
 * The only place a brand is edited: there is no per-workspace brand. The
 * editor is shown inline (not behind a button) — this page exists to edit it.
 * It sits beside Document Sign's signing components because its live preview
 * is the signing page itself.
 */

import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { BrandingSettingsPanel } from '@/arche/arches/document-sign/components';

export function CompanyBrandingCard({ companyId }: { companyId: string }) {
  const branding = useQuery({
    queryKey: queryKeys.companies.branding(companyId),
    queryFn: () => api.getCompanyBranding(companyId),
  });

  if (branding.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <p role="alert" className="text-sm text-destructive">
            Could not load your branding.{' '}
            {branding.error instanceof ApiError ? branding.error.message : 'Please try again.'}
          </p>
          <Button size="sm" variant="outline" onClick={() => void branding.refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!branding.data?.brandingEnabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>
          Your name, mark and colours on the signing pages recipients see, the sign-off on their
          emails, and your Company portals — for every workspace this Company links.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <BrandingSettingsPanel companyId={companyId} />
      </CardContent>
    </Card>
  );
}
